import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    UserCheck,
    Moon,
    AlertTriangle,
    Building2,
    Target,
    MessageSquare,
    Clock,
    Columns,
    List,
    LayoutGrid,
    History,
    Search,
    MapPin,
    Users,
    FileDown
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonnelPage = () => {
    const { token, isChefDispositif } = useAuth();
    const [personnel, setPersonnel] = useState([]);
    const [centers, setCenters] = useState([]);
    const [missions, setMissions] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('columns');
    const [showHistory, setShowHistory] = useState(false);
    const [assignDialog, setAssignDialog] = useState(null);
    const [assignData, setAssignData] = useState({ center_id: '', mission_id: '', comment: '' });
    const [searchTerm, setSearchTerm] = useState('');

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [personnelRes, centersRes, missionsRes] = await Promise.all([
                axios.get(`${API}/personnel/status`, { headers }),
                axios.get(`${API}/care-centers`, { headers }),
                axios.get(`${API}/missions`, { headers }).catch(() => ({ data: [] }))
            ]);
            setPersonnel(personnelRes.data);
            setCenters(centersRes.data);
            setMissions(missionsRes.data);
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await axios.get(`${API}/personnel/history`, { headers });
            setHistory(res.data);
        } catch (e) {
            toast.error('Erreur chargement historique');
        }
    };

    useEffect(() => { fetchData(); const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, []);

    const handleToggle = async (userId) => {
        try {
            const res = await axios.put(`${API}/personnel/${userId}/toggle`, { comment: '' }, { headers });
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleDemobilize = async (userId) => {
        try {
            const res = await axios.put(`${API}/personnel/${userId}/demobilize`, { comment: '' }, { headers });
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleAssign = async () => {
        if (!assignDialog) return;
        try {
            await axios.put(`${API}/personnel/${assignDialog}/assign`, assignData, { headers });
            toast.success('Affectation mise a jour');
            setAssignDialog(null);
            setAssignData({ center_id: '', mission_id: '', comment: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const openAssignDialog = (p) => {
        setAssignDialog(p.user_id);
        setAssignData({
            center_id: p.center_id || '',
            mission_id: p.mission_id || '',
            comment: p.comment || ''
        });
    };

    const filtered = useMemo(() => {
        if (!searchTerm) return personnel;
        const lower = searchTerm.toLowerCase();
        return personnel.filter(p => 
            p.user_name.toLowerCase().includes(lower) || 
            p.role.toLowerCase().includes(lower) ||
            (p.center_name || '').toLowerCase().includes(lower) ||
            (p.mission_name || '').toLowerCase().includes(lower)
        );
    }, [personnel, searchTerm]);

    const actifs = filtered.filter(p => p.status === 'actif');
    const repos = filtered.filter(p => p.status === 'repos');
    const nonMobilises = filtered.filter(p => p.status === 'non_mobilise');

    const roleLabels = {
        chef_dispositif: 'Chef de dispositif',
        logistique: 'Logistique',
        moyen: 'Moyens',
        secouriste: 'Secouriste',
        gymnase_admin: 'Admin Gymnase',
        gymnase_chef_poste: 'Chef de Poste',
        benevole: 'Benevole',
        commune: 'Commune',
        equipier: 'Equipier',
        responsable_technique: 'Resp. Technique',
        agent_communaux: 'Agent communaux',
        reserve_securite_civile: 'Reserve securite civile',
        entreprise_exterieur: 'Entreprise ext.',
        entreprise_exterieur_contrat: 'Entreprise ext. contrat'
    };

    const PersonCard = ({ p, compact = false }) => (
        <div 
            className={`p-3 rounded-lg border transition-all ${
                p.status === 'actif' 
                    ? p.alert_12h 
                        ? 'bg-red-500/10 border-red-500/40' 
                        : 'bg-emerald-500/8 border-emerald-500/30'
                    : p.status === 'repos'
                        ? 'bg-zinc-800/40 border-zinc-700/40'
                        : 'bg-blue-500/5 border-blue-500/20'
            }`}
            data-testid={`personnel-card-${p.user_id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">{p.user_name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300">{roleLabels[p.role] || p.role}</span>
                        {p.alert_12h && (
                            <span className="flex items-center gap-1 text-[10px] text-red-400 font-semibold">
                                <AlertTriangle className="w-3 h-3" /> FATIGUE &gt;12h
                            </span>
                        )}
                    </div>
                    {p.status === 'actif' && (
                        <div className="mt-1.5 space-y-0.5">
                            {p.duration_hours > 0 && (
                                <p className="text-[11px] text-zinc-400 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Actif depuis {p.duration_hours}h
                                    {p.activated_at && (
                                        <span className="text-zinc-600">
                                            ({new Date(p.activated_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })})
                                        </span>
                                    )}
                                </p>
                            )}
                            {p.center_name && (
                                <p className="text-[11px] text-cyan-400 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" /> {p.center_name}
                                </p>
                            )}
                            {p.mission_name && (
                                <p className="text-[11px] text-amber-400 flex items-center gap-1">
                                    <Target className="w-3 h-3" /> {p.mission_name}
                                </p>
                            )}
                            {p.comment && (
                                <p className="text-[11px] text-zinc-500 flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" /> {p.comment}
                                </p>
                            )}
                        </div>
                    )}
                </div>
                {isChefDispositif && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                        <Button
                            size="sm"
                            onClick={() => handleToggle(p.user_id)}
                            className={`text-xs h-8 px-3 font-medium ${
                                p.status === 'actif' 
                                    ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-200' 
                                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            }`}
                            data-testid={`toggle-${p.user_id}`}
                        >
                            {p.status === 'actif' ? 'Repos' : 'Mobiliser'}
                        </Button>
                        {p.status === 'actif' && (
                            <Button
                                size="sm"
                                onClick={() => openAssignDialog(p)}
                                className="text-xs h-8 px-3 font-medium bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-600/30"
                                data-testid={`assign-${p.user_id}`}
                            >
                                Affecter
                            </Button>
                        )}
                        {(p.status === 'actif' || p.status === 'repos') && (
                            <Button
                                size="sm"
                                onClick={() => handleDemobilize(p.user_id)}
                                className="text-xs h-8 px-3 font-medium bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30"
                                data-testid={`demobilize-${p.user_id}`}
                            >
                                Demobiliser
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // Vue par Affectation (missions et centres)
    const activeMissions = missions.filter(m => m.status === 'active');
    const openCenters = centers.filter(c => c.status === 'open');
    const unassigned = actifs.filter(p => !p.center_id && !p.mission_id);

    const personnelByMission = activeMissions.map(m => ({
        ...m,
        personnel: actifs.filter(p => p.mission_id === m.id)
    }));

    const personnelByCenter = openCenters.map(c => ({
        ...c,
        personnel: actifs.filter(p => p.center_id === c.id)
    }));

    if (loading) return <div className="p-6 text-center text-zinc-500">Chargement...</div>;

    return (
        <div className="page-container" data-testid="personnel-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="page-title">Personnel</h1>
                    <p className="page-subtitle">Suivi des effectifs actifs et au repos</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                            <UserCheck className="w-4 h-4 text-emerald-400" />
                            <span className="text-emerald-400 font-semibold">{actifs.length}</span>
                            <span className="text-zinc-400 text-xs">actifs</span>
                        </span>
                        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700">
                            <Moon className="w-4 h-4 text-zinc-500" />
                            <span className="text-zinc-400 font-semibold">{repos.length}</span>
                            <span className="text-zinc-500 text-xs">repos</span>
                        </span>
                        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30">
                            <Users className="w-4 h-4 text-blue-400" />
                            <span className="text-blue-400 font-semibold">{nonMobilises.length}</span>
                            <span className="text-zinc-400 text-xs">non mob.</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Controls bar */}
            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Rechercher..."
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm"
                        data-testid="personnel-search"
                    />
                </div>
                <div className="flex items-center rounded-lg border border-zinc-700 overflow-hidden">
                    {[
                        { key: 'columns', icon: Columns, label: 'Colonnes' },
                        { key: 'list', icon: List, label: 'Liste' },
                        { key: 'assignment', icon: LayoutGrid, label: 'Affectation' }
                    ].map(v => (
                        <button
                            key={v.key}
                            onClick={() => setView(v.key)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors ${
                                view === v.key ? 'bg-cyan-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                            }`}
                            data-testid={`view-${v.key}`}
                        >
                            <v.icon className="w-3.5 h-3.5" />
                            {v.label}
                        </button>
                    ))}
                </div>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setShowHistory(true); fetchHistory(); }}
                    className="text-xs text-zinc-400 hover:text-white"
                    data-testid="show-history-btn"
                >
                    <History className="w-3.5 h-3.5 mr-1" /> Historique
                </Button>
            </div>

            {/* VIEW: COLUMNS */}
            {view === 'columns' && (
                <div className="grid grid-cols-3 gap-4" data-testid="view-columns-content">
                    {/* Actifs Column */}
                    <Card className="content-card">
                        <CardHeader className="card-header">
                            <CardTitle className="card-title flex items-center gap-2 text-emerald-400">
                                <UserCheck className="w-5 h-5" />
                                Actifs ({actifs.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                            {actifs.length > 0 ? actifs.map(p => (
                                <PersonCard key={p.user_id} p={p} />
                            )) : (
                                <p className="text-zinc-500 text-sm text-center py-8">Aucun personnel actif</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Repos Column */}
                    <Card className="content-card">
                        <CardHeader className="card-header">
                            <CardTitle className="card-title flex items-center gap-2 text-zinc-400">
                                <Moon className="w-5 h-5" />
                                Au repos ({repos.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                            {repos.length > 0 ? repos.map(p => (
                                <PersonCard key={p.user_id} p={p} />
                            )) : (
                                <p className="text-zinc-500 text-sm text-center py-8">Aucun personnel au repos</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Non mobilise Column */}
                    <Card className="content-card">
                        <CardHeader className="card-header">
                            <CardTitle className="card-title flex items-center gap-2 text-blue-400">
                                <Users className="w-5 h-5" />
                                Non mobilises ({nonMobilises.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                            {nonMobilises.length > 0 ? nonMobilises.map(p => (
                                <PersonCard key={p.user_id} p={p} />
                            )) : (
                                <p className="text-zinc-500 text-sm text-center py-8">Tous les personnels sont mobilises</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* VIEW: LIST */}
            {view === 'list' && (
                <Card className="content-card" data-testid="view-list-content">
                    <CardContent className="p-0">
                        <div className="divide-y divide-zinc-800">
                            {filtered.length > 0 ? filtered.map(p => (
                                <div key={p.user_id} className="p-3 hover:bg-zinc-800/30">
                                    <PersonCard p={p} compact />
                                </div>
                            )) : (
                                <p className="text-zinc-500 text-sm text-center py-8">Aucun resultat</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* VIEW: ASSIGNMENT */}
            {view === 'assignment' && (
                <div className="space-y-4" data-testid="view-assignment-content">
                    {/* By Mission */}
                    {personnelByMission.map(m => (
                        <Card key={m.id} className="content-card">
                            <CardHeader className="card-header py-2 px-4">
                                <CardTitle className="card-title text-sm flex items-center gap-2">
                                    <Target className="w-4 h-4 text-amber-400" />
                                    <span className="text-amber-400">Mission:</span> {m.title}
                                    <Badge variant="outline" className="ml-2 text-[10px]">{m.personnel.length} actif{m.personnel.length > 1 ? 's' : ''}</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 space-y-2">
                                {m.personnel.length > 0 ? m.personnel.map(p => (
                                    <PersonCard key={p.user_id} p={p} compact />
                                )) : (
                                    <p className="text-zinc-600 text-xs text-center py-2">Aucun personnel affecte</p>
                                )}
                            </CardContent>
                        </Card>
                    ))}

                    {/* By Center */}
                    {personnelByCenter.map(c => (
                        <Card key={c.id} className="content-card">
                            <CardHeader className="card-header py-2 px-4">
                                <CardTitle className="card-title text-sm flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-cyan-400" />
                                    <span className="text-cyan-400">Centre:</span> {c.name}
                                    <Badge variant="outline" className="ml-2 text-[10px]">{c.personnel.length} actif{c.personnel.length > 1 ? 's' : ''}</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 space-y-2">
                                {c.personnel.length > 0 ? c.personnel.map(p => (
                                    <PersonCard key={p.user_id} p={p} compact />
                                )) : (
                                    <p className="text-zinc-600 text-xs text-center py-2">Aucun personnel affecte</p>
                                )}
                            </CardContent>
                        </Card>
                    ))}

                    {/* Unassigned */}
                    {unassigned.length > 0 && (
                        <Card className="content-card">
                            <CardHeader className="card-header py-2 px-4">
                                <CardTitle className="card-title text-sm flex items-center gap-2 text-zinc-400">
                                    <MapPin className="w-4 h-4" />
                                    Non affectes
                                    <Badge variant="outline" className="ml-2 text-[10px]">{unassigned.length}</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 space-y-2">
                                {unassigned.map(p => (
                                    <PersonCard key={p.user_id} p={p} compact />
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* ASSIGN DIALOG */}
            <Dialog open={!!assignDialog} onOpenChange={(open) => { if (!open) setAssignDialog(null); }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="assign-dialog">
                    <DialogHeader>
                        <DialogTitle>Affecter le personnel</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div className="form-group">
                            <Label className="form-label">Centre CARE</Label>
                            <Select value={assignData.center_id || 'none'} onValueChange={(v) => setAssignData({...assignData, center_id: v === 'none' ? '' : v})}>
                                <SelectTrigger className="tactical-input" data-testid="assign-center"><SelectValue placeholder="Aucun" /></SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    <SelectItem value="none">Aucun</SelectItem>
                                    {centers.filter(c => c.status === 'open').map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Mission</Label>
                            <Select value={assignData.mission_id || 'none'} onValueChange={(v) => setAssignData({...assignData, mission_id: v === 'none' ? '' : v})}>
                                <SelectTrigger className="tactical-input" data-testid="assign-mission"><SelectValue placeholder="Aucune" /></SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    <SelectItem value="none">Aucune</SelectItem>
                                    {missions.filter(m => m.status === 'active').map(m => (
                                        <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Commentaire</Label>
                            <input
                                type="text"
                                value={assignData.comment}
                                onChange={(e) => setAssignData({...assignData, comment: e.target.value})}
                                className="w-full px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                                placeholder="Disponible PC avance..."
                                data-testid="assign-comment"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={() => setAssignDialog(null)}>Annuler</Button>
                            <Button onClick={handleAssign} className="tactical-btn" data-testid="assign-submit">Enregistrer</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* HISTORY DIALOG */}
            <Dialog open={showHistory} onOpenChange={setShowHistory}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh]" data-testid="history-dialog">
                    <DialogHeader>
                        <div className="flex items-center justify-between w-full">
                            <DialogTitle className="flex items-center gap-2">
                                <History className="w-5 h-5 text-cyan-500" />
                                Historique des activations
                            </DialogTitle>
                            {history.length > 0 && (
                                <a href={`${API}/personnel/history/export?token=${token}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-600/30 transition-colors"
                                    data-testid="export-history-btn">
                                    <FileDown className="w-3.5 h-3.5" /> Exporter CSV
                                </a>
                            )}
                        </div>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh] mt-2">
                        {history.length > 0 ? (
                            <div className="space-y-2">
                                {history.map((h, i) => (
                                    <div key={h.id || i} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50" data-testid={`history-${h.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-white">{h.user_name}</span>
                                                <Badge className={`text-[10px] ${h.action === 'actif' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : h.action === 'non_mobilise' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-zinc-700 text-zinc-400 border-zinc-600'}`}>
                                                    {h.action === 'actif' ? 'Active' : h.action === 'non_mobilise' ? 'Demobilise' : 'Mis au repos'}
                                                </Badge>
                                            </div>
                                            <span className="text-[10px] text-zinc-500">
                                                {new Date(h.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-zinc-500 mt-1">
                                            par {h.changed_by}
                                            {h.comment && <span> — {h.comment}</span>}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-zinc-500 text-sm text-center py-8">Aucun historique</p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PersonnelPage;
